'use client';

import Link from 'next/link';

import { Accordion } from '@gds/Accordion/Accordion';
import Button from '@gds/Button/Button';
import { useModal } from '@modal/Hooks/UseModal';

import { LegalBasisModal } from './LegalBasisModal';
import { PRIVACY_TERMS_SITES } from './PrivacyTerms.constants';

import styles from './Privacy.module.css';

const PrivacyTerms = () => {
  const { openModal, closeModal } = useModal();

  const openMoreInfoModal = () => {
    openModal(<LegalBasisModal closeModal={closeModal} />, {
      header: true,
      modalBodyClassName: styles.modalBody,
    });
  };

  return (
    <div className={styles.pageWrapper}>
      <h1>Drive Privacy Policy</h1>
      <p>
        At Drive, we value transparency and your privacy. This page has everything you need to know
        about how we use your data. If you have any questions about our Privacy Policy, we’d love to
        hear from you - just email{' '}
        <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
          privacy@drive.co.uk
        </a>
        .
      </p>

      <section className={styles.privacyCallout}>
        <p>
          <strong>
            Please read this carefully to understand how we will collect and use your personal
            information. Our Privacy Policy only takes 10-15 minutes to read.
          </strong>
        </p>

        <p>
          <strong>
            By using our Service you acknowledge you have read and understood this privacy policy.
          </strong>
        </p>
      </section>

      <p>
        We are Volkswagen Financial Services DS (UK) Limited, trading as <strong>Drive</strong>. We
        refer to Drive as &quot;<strong>we</strong>&quot;, &quot;<strong>us</strong>&quot; and
        &quot;
        <strong>our</strong>&quot;. We operate the following sites:
      </p>
      <table className={styles.privacyTable}>
        <tbody>
          {PRIVACY_TERMS_SITES.map(site => (
            <tr key={site.name}>
              <td>{site.name}</td>
              <td>
                <Link
                  {...(site.externalLink && { externalLink: site.externalLink })}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {site.displayUrl || site.url}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        This is the Privacy Policy for each of these websites. We refer to Drive, each the sites
        listed above, and the Drive app (our &quot;<strong>App</strong>&quot;) all together, as the
        &quot;<strong>Service</strong>&quot;.
      </p>
      <p>
        For the purposes of UK and EU data protection law, (the &quot;
        <strong>Data Protection Law</strong>&quot;), the controller of the Service is Drive.
      </p>
      <section className={styles.privacyCallout}>
        <h4>
          This policy (together with our{' '}
          <Link href="/terms-conditions" target="_blank">
            Terms of use
          </Link>
          ) sets out:
        </h4>
        <ul>
          <li>Information we collect about you</li>
          <li>How we use your information</li>
          <li>Our promotional updates and communications</li>
          <li>Who we share your information with</li>
          <li>Where we store your information</li>
          <li>Public forums</li>
          <li>Child safety</li>
          <li>How we protect your information</li>
          <li>How long we keep your information</li>
          <li>Your rights</li>
          <li>Changes to this policy</li>
          <li>Contacting us</li>
        </ul>
      </section>

      <h2>Information we collect about you</h2>
      <section className={styles.privacyCallout}>
        <h4>Customers</h4>
        <p>
          We will collect and process certain personal information if you use our Service, including
          searching for used cars and getting in touch with our selected dealers.
        </p>
      </section>
      <section className={styles.privacyCallout}>
        <h4>Dealers</h4>
        <p>We also process information about you:</p>
        <ul>
          <li>if you, or the company you work for, are a selected dealer of ours.</li>
          <li>if you or the company you work for use our Service generally.</li>
          <li>
            you are someone, or you work for someone, to whom we want to advertise or market our
            Service.
          </li>
        </ul>
      </section>
      <table className={styles.privacyTable}>
        <thead>
          <tr>
            <th />
            <th className={styles.grayBackground}>Customers</th>
            <th className={styles.grayBackground}>Dealers</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.privacyTr}>
            <td>
              <h4>You provide us with</h4>
            </td>
            <td colSpan={2}>
              This is information about you that you provide us directly when you use the Service,
              including if you:
            </td>
          </tr>
          <tr>
            <td />
            <td className={styles.grayBackground}>
              <ul>
                <li>save a search</li>
                <li>Send a lead to a selected dealer to enquire about a vehicle</li>
                <li>register to use the Service</li>
                <li>use our concierge chat</li>
                <li>submit a query to our customer services team</li>
                <li>
                  use our finance options calculator, enter a competition, promotion or survey
                </li>
                <li>enter a competition, prize draw, promotion or survey</li>
                <li>apply for financing via Drive</li>
                <li>when you report a problem with the Service</li>
              </ul>
            </td>
            <td className={styles.grayBackground}>
              <ul>
                <li>contact through our enquiry form online</li>
                <li>contact us through another online platform, like LinkedIn.</li>
                <li>email us, or otherwise directly get in touch with us.</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td />
            <td colSpan={2}>
              <p>
                When you interact with us, we collect the following types of personal information:
              </p>
              <ul>
                <li>
                  <strong>Identity:</strong> including first and last name.
                </li>
                <li>
                  <strong>Contact:</strong> including phone number(s), username, address and e-mail
                  address.
                </li>
                <li>
                  <strong>Interaction:</strong> your preferences, queries and feedback which you
                  provide to us, including via our concierge chat service, contact forms, Facebook
                  messenger chat, social media platforms such as <i>Instagram</i> or <i>Twitter</i>,
                  and any social media comments, shares or posts on Drive&apos;s own accounts or
                  which you tag us in or use any of our hashtags in (for example, to enter a
                  competition or prize draw), and your emails with us and selected dealers via us.
                </li>
                <li>
                  <strong>Car:</strong> if you use our vehicle parts search and referral services
                  (for example on AutoVillage or BuyYourCarParts), we may collect your car make and
                  model, registration number, or other car data which you give us, or we match with
                  the information you give us.
                </li>
                <li>
                  <strong>Finance options:</strong> If you use our finance options calculator,
                  including the part exchange feature, we take information about your current car
                  (like mileage &amp; number plate) and some other details from you (like cash
                  deposit amount, and term).
                </li>
                <li>
                  <strong>Financing application:</strong> If you apply for financing from Volkswagen
                  Financial Services (UK) Limited (VWFS) or another finance provider, we collect the
                  information in the application form (like your Identity, Contact, and employment
                  details, and whether you select a Personal Contract Plan (PCP) or Hire Purchase
                  (HP)) on behalf of the finance provider. We&apos;ll pass on the finance
                  provider&apos;s decision to you.
                </li>
                <li>
                  <strong>Purchase:</strong> If you purchase a car through Drive, we will collect
                  your: nationality, residential status, marital status, number of dependents, type
                  of driving license, residential address, employment, and bank details (name, sort
                  code and account number).
                </li>
                <li>
                  <p>
                    <strong>Dealer:</strong> information we need to work with our dealership and
                    group partners, including
                  </p>
                  <ul>
                    <li>
                      your name, role/job title, your dealership &amp; group, and other{' '}
                      <strong>work Identity</strong> &amp; <strong>Contact</strong> information you
                      give us,
                    </li>
                    <li>
                      <strong>Company Account:</strong> information used to administer your{' '}
                      <strong>company account</strong>, including managing payments and/or billing,
                      and to verify financial transactions.
                    </li>
                    <li>
                      <strong>Business Development:</strong> information about events to which you
                      are invited and preferences related to managing those events (for example
                      dietary information), and information that you give us or we otherwise obtain
                      when you visit us.
                    </li>
                  </ul>
                </li>
              </ul>
            </td>
          </tr>
          <tr className={styles.privacyTr}>
            <td>
              <h4>Other parties may provide us with</h4>
            </td>
            <td colSpan={2}>
              <p>
                We <strong>work closely with third parties</strong> (including car dealerships, car
                manufacturers, financing and insurance providers, sub-contractors in communications,
                technical, and payment services, advertising networks, analytics providers, and
                search information providers).
              </p>
              <p>
                We may share personal information we collect <strong>within Drive</strong>{' '}
                (including our group companies and joint venture partners), or{' '}
                <strong>combine data</strong> from other sources with personal information
                you&apos;ve given to us – for example, if you get in touch with a dealer as part of
                your search for your next car.{' '}
              </p>
              <p>
                We&apos;ll tell you (usually in this Privacy Policy) when we receive information
                about you from another source, and for what purpose(s) we&apos;ll use that
                information.{' '}
              </p>
            </td>
          </tr>
          <tr>
            <td />
            <td className={styles.grayBackground}>
              <p>
                This may involve processing your <strong>identity, contact, interaction</strong> and{' '}
                <strong>finance information</strong>.
              </p>
              <ul>
                <li>
                  <strong>Dealers</strong> may share information about your customer journey with
                  Drive, including your identity, contact, interaction and finance information.{' '}
                </li>
                <li>
                  <strong>Advertising networks and social media</strong> may provide us with
                  analytics information. This is aggregated, and cannot be linked back to you.
                </li>
                <li>
                  <strong>Finance Lenders</strong> will provide Drive with any information that
                  relates to a finance application that originated on the Drive website including
                  your application reference.
                </li>
              </ul>
            </td>
            <td className={styles.grayBackground}>
              <p>
                This may involve processing your{' '}
                <strong>work identity, contact, dealer interaction</strong> and{' '}
                <strong>finance information</strong>.
              </p>
              <p>Personal information which is not collected directly from you may be collected:</p>
              <ul>
                <li>
                  from your employer (e.g. your dealership / group) in connection with your role and
                  its relation to our provisions of the Service.
                </li>
                <li>
                  from third parties we work closely with (including, for example, from business
                  partners, finance lenders, and introductions from other individuals or companies).
                </li>
              </ul>
            </td>
          </tr>
          <tr className={styles.privacyTr}>
            <td>
              <h4>We automatically collect</h4>
            </td>
            <td colSpan={2}>
              <p>
                We will automatically collect information from you each time you use our Service.
                This includes:
              </p>
              <ul>
                <li>
                  <strong>Technical information</strong>
                </li>
                <li>
                  <strong>Information about your visit</strong>
                </li>
                <li>
                  <strong>Location data</strong>
                </li>
              </ul>
              <Accordion title="Find out more">
                <ul>
                  <li>
                    <strong>Technical information</strong> may include the Internet protocol (IP)
                    address used to connect your computer to the Internet, pixel ID, click ID, app
                    ID, access token, your login information, browser type and version, Google ID,
                    time zone setting, browser plug-in types and versions, operating system and
                    platform;
                  </li>
                  <li>
                    <strong>Information about your visit</strong> may include the full Uniform
                    Resource Locators (URL), clickstream to, through and from our site (including
                    date and time), vehicles you viewed or searched for, page response times,
                    download errors, length of visits to certain pages, page interaction information
                    (such as scrolling, clicks, and mouse-overs), event IDs, and methods used to
                    browse away from the page. We may also collect any phone number you use to call
                    our customer service number (include call metadata, like date, time &amp; length
                    of a call), or the social media handle you use to connect with our customer
                    service team. (We may record calls for training &amp; performance purposes, to
                    establish clear records, and for legal reasons. We&apos;ll always tell you
                    beforehand if a call is being recorded.)
                  </li>
                  <li>
                    <strong>Location data</strong> - we collect information through the Service as
                    to your geolocation based on your IP to provide location services where
                    requested or agreed to by you in order to show you local car dealerships, and to
                    deliver content, advertising or other services that are dependent on knowing
                    where you are, like recommending new local car listings. Location data may be
                    collected in combination with an identifier associated with your device, so we
                    can recognise your mobile browser or device when you return to the Service.
                    Delivery of location services will involve reference to one or more of the
                    following: (a) the coordinates (latitude/longitude) of your location; (b)
                    look-up of your country of location by reference to your IP address against
                    public sources; and/or (c) your Identifier for Advertisers (IFA) code for your
                    Apple device, or the Android ID for your Android device, or a similar device
                    identifier.
                  </li>
                </ul>
                <p>You can opt-out of location sharing.</p>
                <em>
                  See our{' '}
                  <Link href="/cookies" target="_blank">
                    Cookie Policy
                  </Link>{' '}
                  for more information on the use of cookies and device identifiers on the Service.
                </em>
              </Accordion>
            </td>
          </tr>
        </tbody>
      </table>

      <h2>How we use your information</h2>
      <p>
        <strong>We use your personal information in different ways.</strong> We&apos;ve set these
        out below, along with the <strong>types of personal information</strong> we use for each
        purpose, and the <strong>legal reason(s)</strong> (also known as the &apos;legal
        basis&apos;) that permits this processing.
      </p>
      <p>
        We explain more about what each legal basis means below. We may process your personal
        information based one or more than one legal basis, depending on the specific ways we are
        using your personal information.
      </p>

      <h3>Customers</h3>
      <br />
      <table className={`${styles.privacyTable} ${styles.borders}`}>
        <thead className={styles.grayBackground}>
          <tr>
            <th>How we use this personal information</th>
            <th>Type of personal information</th>
            <th>Our legal bases for processing</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <p>
                To <strong>perform our contract</strong> with you under our{' '}
                <Link href="/terms-conditions" target="_blank">
                  Terms of Use
                </Link>{' '}
                and <strong>manage our relationship with you</strong>, including:
              </p>
              <ul>
                <li>providing our Service to you, </li>
                <li>administering your account with us (if you create one),</li>
                <li>storing your personal information,</li>
                <li>
                  providing information to you which you have requested (e.g. via our concierge
                  chat),
                </li>
                <li>sending you service updates (including security alerts),</li>
                <li>notifying you about changes to our terms or privacy policy,</li>
                <li>notifying you of any changes to our Service.</li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Account</strong>
                </li>
                <li>
                  <strong>Finance</strong>
                </li>
                <li>
                  <strong>Location</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Contract </li>
                <li>Legal Obligation</li>
                <li>Our legitimate interests</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              To <strong>save searches and send you search alerts</strong> (for example, new
              listings which fit your search criteria).
            </td>
            <td>
              <ul>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Location</strong>
                </li>
                <li>
                  <strong>Automatically collected information</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Consent</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              To put <strong>you in touch with UK depots who may provide</strong> vehicle part
              information to you, the information you provide may be shared with 1st Choice Spares
              and depots across the country so they can contact you about the vehicle part(s) you
              are interested in.
            </td>
            <td>
              <ul>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Car</strong>
                </li>
                <li>
                  <strong>Location (post code)</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Our legitimate interests</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              To <strong>register you</strong> as a new user who wants to use our Service, if you
              choose to create an account. You don&apos;t need to have an account to search for
              vehicles on Drive.
            </td>
            <td>
              <ul>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Contact</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Contract</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              To allow you to purchase a vehicle from a vehicle dealer through Drive or from Drive
              directly, including (as applicable):
              <ul>
                <li>
                  <strong>obtaining any approvals needed</strong>
                </li>
                <li>
                  <strong>providing your information to the dealer,</strong>
                </li>
                <li>
                  <strong>connecting you with finance providers, and</strong>
                </li>
                <li>
                  <strong>processing payment (e.g. receiving a bank transfer from you).</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Account</strong>
                </li>
                <li>
                  <strong>Location</strong>
                </li>
                <li>
                  <strong>Purchase</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Contract</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>To allow you to apply for financing with VWFS (or other financing providers)</td>
            <td>
              <ul>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Financing application</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Our legitimate interests</li>
                <li>Contract</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                To <strong>promote our service</strong> to you and <strong>keep you updated</strong>{' '}
                with information about our services, including:
              </p>
              <ul>
                <li>
                  delivering relevant content on topics we think may be of interest to you, and
                </li>
                <li>letting you know about new functionalities or services.</li>
              </ul>
              <p>
                To <strong>ask you to leave a review, provide feedback or take a survey</strong>.
              </p>

              <p>
                To <strong>offer you exclusive deals and offers</strong>.
              </p>

              <em>
                See <a href="#promotional-updates">Our promotional updates and communications</a> to
                learn more
              </em>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Identity </strong>
                </li>
                <li>
                  <strong>Contact </strong>
                </li>
                <li>
                  <strong>Account</strong>
                </li>
                <li>
                  <strong>Automatically collected information</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Consent </li>
                <li>Our legitimate interests</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              To <strong>manage and administer promotions, competitions and prize draws</strong>{' '}
              which you enter, subject to their terms and conditions which will be notified to you
              when you enter, including collecting and checking entries, contacting winners,
              awarding and delivering any prizes, and complying with our regulatory obligations.
            </td>
            <td>
              <ul>
                <li>Identity</li>
                <li>Contact</li>
                <li>Interaction</li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Consent</li>
                <li>Legal obligation</li>
                <li>Our legitimate interests</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                To <strong>customise your experience</strong> and offer you relevant ads, content
                and recommendations including information about new promotions and competitions.
              </p>
              <p>
                To deliver dynamic{' '}
                <strong>marketing and show you non-personalised ads on our website.</strong>
              </p>
              <p>
                <strong>Social media integrations:</strong> Our marketing campaigns use
                Facebook&apos;s advertising and audiences tools, including tracking pixels, to
                inform and refine our marketing and deliver more relevant ads to you.
              </p>
              <p>
                For{' '}
                <strong>
                  measuring, understanding and optimising the effectiveness of advertising
                </strong>{' '}
                we serve to you and others, and to deliver relevant advertising to you.
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Contact </strong>
                </li>
                <li>
                  <strong>Account</strong>
                </li>
                <li>
                  <strong>Automatically collected information</strong>
                </li>
                <li>
                  <strong>Identity</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Our legitimate interests</li>
                <li>
                  Consent (to{' '}
                  <Link href="/cookies" target="_blank">
                    Cookies
                  </Link>
                  )
                </li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                To <strong>administer our Service</strong> and for internal operations, including
                bug fixes, troubleshooting, data analysis, testing, and survey purposes.
              </p>
              <p>
                To <strong>improve our Service</strong> to ensure that content is presented in the
                most effective manner for you and for your computer. We may also use aggregated
                information to help us plan and introduce new features.
              </p>
              <p>
                To <strong>keep our Service safe and secure</strong>, including taking steps to
                detect and prevent fraud.
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Interaction</strong>
                </li>
                <li>
                  <strong>Automatically collected information </strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Our legitimate interests</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                To <strong>analyse</strong> our user base, Service and service usage, feature and
                content popularity, software and hardware quality, and market trends.
              </p>
              <p>
                To <strong>share aggregated and anonymised statistics with dealers</strong> and
                other organisations about how people use Drive, including in order to attribute
                leads. You cannot be identified from this information.
              </p>
              <p>
                We may also use this information to inform, refine and deliver our wider online
                marketing campaigns, including delivering our advertisements to you when you are not
                on our Service.{' '}
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Identity </strong>
                </li>
                <li>
                  <strong>Contact </strong>
                </li>
                <li>
                  <strong>Account</strong>
                </li>
                <li>
                  <strong>Interaction</strong>
                </li>
                <li>
                  <strong>Automatically collected information </strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Our legitimate interests</li>
                <li>
                  Consent (to{' '}
                  <Link href="/cookies" target="_blank">
                    Cookies
                  </Link>
                  )
                </li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Selected dealers</h3>
      <br />
      <table className={`${styles.privacyTable} ${styles.borders}`}>
        <thead className={styles.grayBackground}>
          <tr>
            <th>How we use this personal information</th>
            <th>Type of personal information</th>
            <th>Our legal bases for processing</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={3}>
              If you work for one of our selected dealers, or are otherwise interacting with us in a
              business context, we may also use your personal information for the following
              purposes.
            </td>
          </tr>
          <tr>
            <td>
              To <strong>perform or enter into any contract</strong> we have with you
            </td>
            <td>
              <ul>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Company Account</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Contract</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                To <strong>administrate</strong> yours or your company&apos;s accounts with us.
              </p>
              <p>
                To <strong>make and verify</strong> payments and bills.
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Contact</strong>
                </li>
                <li>
                  <strong>Identity</strong>
                </li>
                <li>
                  <strong>Company Account</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Contract</li>
                <li>Our legitimate interests</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                In the event that <strong>we sell or buy any business or assets</strong>, disclose
                your data to a prospective seller or buyer, or in the event we are the subject of
                acquisition by a third party.
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Information relating to transactions</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Contract</li>
                <li>Legal obligation</li>
                <li>Our legitimate interests</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
          <tr>
            <td>
              <p>
                To <strong>answer any queries</strong> you send us
              </p>
              <p>
                To <strong>comply with any rights requests</strong> you make
              </p>
            </td>
            <td>
              <ul>
                <li>
                  <strong>Contact information</strong>
                </li>
              </ul>
            </td>
            <td>
              <ul>
                <li>Consent</li>
                <li>Legal obligation</li>
              </ul>
              <Button onClick={openMoreInfoModal}>More info</Button>
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        Where we receive your personal information from a third party, they will be a separate data
        controller of your personal information, and will have their own legal bases for processing.
      </p>
      {/** biome-ignore lint/a11y/useAnchorContent: for internal linking */}
      {/** biome-ignore lint/a11y/useValidAnchor: for internal linking */}
      <a id="promotional-updates" className={styles.anchor}></a>
      <h2>Our promotional updates and communications</h2>
      <p>
        Where permitted in our legitimate interests (explained below) or with your prior consent if
        required by law, we may
      </p>
      <ul>
        <li>
          send you marketing and promotional updates about our products and services, by email and
          SMS, if you have opted-in to receiving these.
        </li>
        <li>
          use your work contact information, if you work for a dealer, to send you marketing and
          promotional updates about our products and services, by phone, email and SMS, unless you
          have opted out of receiving these.
        </li>
        <li>we will also use your personal information for marketing analysis.</li>
      </ul>

      <p>
        You can object to further marketing at any time by selecting the &quot;unsubscribe&quot;
        link at the end of all our marketing and promotional update communications to you, or by
        sending us an email to{' '}
        <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
          privacy@drive.co.uk
        </a>
        .
      </p>

      <h2>Who we may share your information with</h2>
      <p>We may give your information to:</p>
      <ul>
        <li>
          Any member of the Drive group, which means our shareholders, investors, subsidiaries, our
          ultimate holding company and its subsidiaries, who support our processing of personal
          information under this policy. If any of these parties use your information for direct
          marketing, we will only transfer it to them for that purpose with your prior consent.
        </li>
        <li>
          Our partner and support businesses, including support services, finance lenders, insurance
          providers (only where we have obtained your consent), advertising networks, prize draw or
          competition partners, and analytics providers, as well as car dealers and depots (only if
          you ask for more detail about a car or vehicle part, or to be put in touch with them).
        </li>
      </ul>
      <Accordion title="Find out more">
        <p>Our selected third parties may include:</p>
        <ul>
          <li>
            <p>
              <strong>Dealers and depots</strong> who, if you ask us to, we will share your
              information with in order for them to contact you about the car, vehicle part or other
              request you submitted.
            </p>
            <ul>
              <li>
                Dealers: if you enquire about a vehicle, we will share your information with the
                dealer of that vehicle.
              </li>
              <li>
                Depots: if you enquire about a car part, will share your information with 1st Choice
                Spares UK Ltd and depots across the UK.
              </li>
            </ul>
            These organisations will only use your information to the extent necessary to perform
            their support functions.
          </li>
          <li>
            <p>
              <strong>Financing providers</strong> (like VWFS) who, if you apply for financing, we
              will share your application information with so you can apply for and arrange PCP or
              HP financing when you buy a vehicle.
            </p>
          </li>
          <li>
            <p>
              <strong>
                Organisations who process your personal information in order to provide a service to
                us
              </strong>
              . This includes third party suppliers, agents, sub-contractors and/or other companies
              in our group who support the services we offer through the Service, including those:
            </p>
            <ul>
              <li>providing website and data hosting services,</li>
              <li>providing fulfilment services,</li>
              <li>distributing any communications we send,</li>
              <li>supporting or updating marketing lists,</li>
              <li>facilitating feedback on our services, and</li>
              <li>providing IT support services.</li>
            </ul>
            These organisations will only use your information to the extent necessary to perform
            their support functions.
          </li>
          <li>
            <p>
              <strong>Advertisers and advertising networks</strong> that require the data to serve
              our adverts to you across other websites.
            </p>
            <ul>
              <li>
                We don’t disclose identifiable information about you to our advertisers. We provide
                them with aggregate information about our users (for example, we may inform them
                that 500 women aged under 30 have clicked on their advertisement on any given day).
              </li>
              <li>
                We may use the personal information we collect from you to enable our advertisers to
                display our ads to people we think might be interested.
                <br />
                <br />
                <em>
                  See our{' '}
                  <Link href="/cookies" target="_blank">
                    Cookies Policy
                  </Link>{' '}
                  for more information.
                </em>
              </li>
            </ul>
          </li>
          <li>
            <strong>Prize draw or competition partners</strong> to the extent required to administer
            and manage prize draws or competitions, including contacting winners and awarding any
            prizes.
          </li>
          <li>
            <strong>Analytics and search engine providers</strong> that assist us in the improvement
            and optimisation of our site (this will not identify you as an individual). Interested.{' '}
            <em>
              See our{' '}
              <Link href="/cookies" target="_blank">
                Cookies Policy
              </Link>{' '}
              for more information.
            </em>
          </li>
          <li>
            <strong>Credit reference agencies</strong> for the purpose of assessing your credit
            score where this is a condition of us entering into a contract with you.
          </li>
        </ul>
        <p>We will also share your personal information with third parties:</p>
        <ul>
          <li>
            If we <strong>sell or buy any business or assets</strong>, in which case we will
            disclose your personal information to the prospective seller or buyer of such business
            or assets subject to the terms of this privacy policy.
          </li>
          <li>
            If Drive or substantially all of its assets are{' '}
            <strong>acquired by a third party</strong>, in which case personal information held by
            it about its customers will be one of the transferred assets.
          </li>
          <li>
            If we are under a duty to disclose or share your personal information in order to comply
            with any <strong>legal obligation</strong>, or in order to enforce or apply our terms of
            supply terms and other agreements with you; or to protect the rights, property, or
            safety of Drive, our customers, or others. This includes exchanging information with
            other companies and organisations for the purposes of <strong>fraud protection</strong>{' '}
            and <strong>credit risk reduction</strong> and to <strong>prevent cybercrime</strong>.
          </li>
        </ul>
      </Accordion>
      <h2>Where we store your information</h2>
      <p>
        We may transfer and store your personal information outside the United Kingdom (&quot;
        <strong>UK</strong>
        &quot;) and/or European Economic Area (&quot;
        <strong>EEA</strong>
        &quot;) in countries which may not have equivalent Data Protection Law.
      </p>
      <p>
        Where your information is transferred outside the UK or EEA, we will take steps to ensure
        your personal information is protected by appropriate legal safeguards, and that it is
        treated securely and in accordance with this privacy policy.
      </p>
      <Accordion title="Find out more">
        <p>We may transfer your personal information outside the UK or EEA:</p>
        <ul>
          <li>
            In order to <strong>store</strong> it.
          </li>
          <li>
            In order to enable us to <strong>provide our Service</strong> to you and fulfil our
            contract with you. This includes the provision of support services, including our
            concierge chat service and other customer service support.
          </li>
          <li>Where we are legally required to do so.</li>
          <li>
            In order to <strong>facilitate the operation of our group of businesses</strong>, where
            it is in our legitimate interests and we have concluded these are not overridden by your
            rights.
          </li>
        </ul>
        <p>
          We may transfer your personal information to the following countries and using the
          following safeguards outside the UK or EEA:
        </p>
        <table className={styles.privacyInlineTable}>
          <tbody>
            <tr>
              <td>
                Amazon.com, Inc.
                <br />
                Amazon Web Services, Inc.
              </td>
              <td>
                <a
                  target="_blank"
                  href="https://d1.awsstatic.com/legal/aws-gdpr/AWS_GDPR_DPA.pdf"
                  rel="noopener"
                >
                  Standard Contractual Clauses
                </a>
              </td>
            </tr>
            <tr>
              <td>The Rocket Science Group LLC d/b/a MailChimp</td>
              <td>Standard Contractual Clauses</td>
            </tr>
            <tr>
              <td>MailChimp</td>
              <td>
                <a
                  target="_blank"
                  href="https://mailchimp.com/legal/data-processing-addendum/"
                  rel="noopener"
                >
                  Standard Contractual Clauses
                </a>
              </td>
            </tr>
            <tr>
              <td>Intercom, Inc.</td>
              <td>Standard Contractual Clauses</td>
            </tr>
            <tr>
              <td>Facebook, Inc.</td>
              <td>
                <a
                  target="_blank"
                  href="https://www.facebook.com/legal/terms/dataprocessing"
                  rel="noopener"
                >
                  Standard Contractual Clauses
                </a>
              </td>
            </tr>
            <tr>
              <td>Google LLC</td>
              <td>
                <a
                  target="_blank"
                  href="https://cloud.google.com/security/compliance/eu-mcc"
                  rel="noopener"
                >
                  Standard Contractual Clauses
                </a>
              </td>
            </tr>
            <tr>
              <td>Hubspot, Inc.</td>
              <td>
                <a target="_blank" href="https://legal.hubspot.com/dpa" rel="noopener">
                  Standard Contractual Clauses
                </a>
              </td>
            </tr>
            <tr>
              <td>Twilio Inc.</td>
              <td>
                <a
                  target="_blank"
                  href="https://www.twilio.com/legal/data-protection-addendum"
                  rel="noopener"
                >
                  Standard Contractual Clauses
                </a>
              </td>
            </tr>
            <tr>
              <td>Aircall</td>
              <td>
                <a
                  target="_blank"
                  href="https://aircall.io/Aircall-DPA-7-24-2020.pdf"
                  rel="noopener"
                >
                  Standard Contractual Clauses
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </Accordion>

      <h2>Child safety</h2>
      <p>Protecting the safety of children when they use the Internet is important to us.</p>
      <p>
        <em>The Service is intended for use only by persons who are at least 18 years of age.</em>
      </p>
      <p>
        <em>By using the Service, you confirm to us that you meet this requirement.</em> If you are
        under the age of 18, you confirm you have received permission from your parent or guardian
        before using this site/App or sending us personal information.
      </p>
      <p>
        If you are under the age of 18 your parent or guardian must consent on your behalf where we
        ask for consent in relation to the use of your information.
      </p>

      <h2>How we protect your information</h2>
      <p>
        All information you provide to us is stored on our secure servers. Where we have given you
        (or where you have chosen) a password which enables you to access certain parts of the
        Service, you are responsible for keeping this password confidential. We ask you not to share
        a password with anyone.
      </p>
      <p>
        Unfortunately, the transmission of information via the internet is not completely secure.
        Although we will do our best to protect your personal information, including using SSL
        encryption to secure our site and protection your information in transit We cannot guarantee
        the security of your data transmitted to the Service; any transmission is at your own risk.
        Once we have received your information, we will use strict procedures and security features
        to try to prevent unauthorised access.
      </p>
      <p>
        Our site may, from time to time, contain links to external sites. We are not responsible for
        the privacy policies or the content of such sites.
      </p>

      <h2>How long we keep your information</h2>
      <p>We keep personal information for </p>
      <ul>
        <li>
          as long as you have an account with us or use our Service (including if you enter a prize
          draw, competition or other promotion) in order to meet our contractual obligations to you
          and{' '}
        </li>
        <li>six years after that to identify any issues and resolve any legal proceedings. </li>
      </ul>
      <p>
        We may also retain aggregate information beyond this time for research purposes and to help
        us develop and improve our services. You cannot be identified from aggregate information
        retained or used for these purposes.
      </p>

      <h2>Your rights</h2>
      <p>You have the right under certain circumstances:</p>
      <ul>
        <li>
          to be provided with a <strong>copy</strong> of your personal information held by us;
        </li>
        <li>
          to request the <strong>correction</strong> or <strong>erasure</strong> of your personal
          information held by us;
        </li>
        <li>
          to request that we <strong>restrict</strong> the processing of your personal information
          (while we verify or investigate your concerns with this information, for example);
        </li>
        <li>
          to <strong>object</strong> to the further processing of your personal information,
          including the right to object to marketing (as mentioned in{' '}
          <a href="#promotional-updates">&apos;Our promotional updates and communications&apos;</a>{' '}
          section;
        </li>
        <li>
          to request that your provided personal information be <strong>moved</strong> to a third
          party.
        </li>
        <li>
          Your right to <strong>withdraw consent:</strong>
          <br />
          <br />
          Where the processing of your personal information by us is based on consent, you have the
          right to withdraw that consent without detriment at any time by contacting us at{' '}
          <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
            privacy@drive.co.uk
          </a>
          . You can also change your marketing preferences at any time as described in{' '}
          <a href="#promotional-updates">Our promotional updates and communications</a> section;
        </li>
      </ul>
      <p>
        You can also exercise the rights listed above at any time by contacting us at{' '}
        <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
          privacy@drive.co.uk
        </a>
        .
      </p>
      <p>
        The{' '}
        <Link href="https://ico.org.uk/" target="_blank">
          Information Commissioner
        </Link>{' '}
        is the supervisory authority in the UK and can provide further information about your rights
        and our obligations in relation to your personal information, as well as deal with any
        complaints that you have about our processing of your personal information.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        Any changes we make to our privacy policy in future will be posted on this page and, in
        relation to substantive changes, will be notified to you by e-mail. This policy was last
        updated on 27 Jul 2026.
      </p>

      <h2>Contacting Us</h2>
      <p>
        Questions, comments and requests regarding this privacy policy are welcomed and should be
        addressed to:
      </p>
      <a href="mailto:privacy@drive.co.uk" target="_blank" rel="noopener">
        privacy@drive.co.uk
      </a>
      <p>Brunswick Court Yeomans Drive, Blakelands, Milton Keynes MK14 5LR</p>
    </div>
  );
};

export default PrivacyTerms;
